import styled, { createGlobalStyle, css } from 'styled-components'
import { NavLink } from 'react-router-dom'

// ===== 設計 token（對齊參考站 Tailwind v4 + shadcn 色碼） =====
export const t = {
  bg: '#fcfdff',
  bgDark: '#020618',
  foreground: '#020617',      // slate-950 (主文字)
  slate950: '#020617',
  slate900: '#0f172a',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',

  blue50: '#eff6ff',
  blue100: '#dbeafe',
  blue200: '#bedbff',
  blue300: '#90c5ff',
  blue400: '#54a2ff',
  blue500: '#3b82f6',
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  blue900: '#172554',
  sky400: '#38bdf8',

  white: '#ffffff',
  white92: 'rgba(255,255,255,0.92)',
  white80: 'rgba(255,255,255,0.8)',
  white75: 'rgba(255,255,255,0.75)',
  white60: 'rgba(255,255,255,0.6)',
  white12: 'rgba(255,255,255,0.12)',
  white8: 'rgba(255,255,255,0.08)',

  borderSubtle: 'rgba(15,23,42,0.06)',     // slate-900/6
  borderSoft: 'rgba(15,23,42,0.08)',       // slate-900/8

  // 對齊原站 shadow 值
  shadowCard: '0 18px 45px rgba(15,23,42,0.06)',
  shadowSoft: '0 18px 45px rgba(15,23,42,0.06)',      // alias
  shadowMedium: '0 20px 40px rgba(15,23,42,0.08)',    // alias
  shadowHover: '0 12px 30px rgba(15,23,42,0.05)',
  shadowBtn: '0 24px 55px rgba(37,99,235,0.2)',
  shadowBtnSm: '0 18px 40px rgba(37,99,235,0.18)',
  shadowBtnLight: '0 22px 55px rgba(56,189,248,0.18)',
  shadowBadge: '0 18px 40px rgba(15,23,42,0.18)',
  shadowDarkCard: '0 34px 90px rgba(15,23,42,0.18)',

  radiusMd: '1.1rem',
  radiusLg: '1.5rem',
  radiusXl: '1.75rem',
  radius2xl: '2.25rem',

  // 原站 README 指定 heading 與 body 都用 IBM Plex Sans
  fontHeading: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
  fontBody: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
} as const

// ===== Global scope =====
export const NoxGlobalScope = createGlobalStyle`
  .nox-root {
    font-family: ${t.fontBody};
    color: ${t.foreground};
    background: ${t.bg};
    font-feature-settings: "cv02", "cv03", "cv04", "cv11";
    -webkit-font-smoothing: antialiased;
  }
  .nox-root h1, .nox-root h2, .nox-root h3, .nox-root h4 {
    font-family: ${t.fontHeading};
    margin: 0;
    /* 不在此設定 color，讓各 section 的 styled component 自行控制（避免 global 蓋掉深底白字） */
  }
  .nox-root p { margin: 0; }
  .nox-root * { box-sizing: border-box; }
`

// ===== primitives =====

export const Root = styled.div`
  min-height: 100vh;
  color: ${t.slate900};
  background-color: #f4f8ff;
  background-image: linear-gradient(rgba(255,255,255,0.72), rgba(244,248,255,0.72));
  font-family: ${t.fontBody};
  -webkit-font-smoothing: antialiased;
`

export const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  @media (min-width: 640px) {
    padding-left: 24px;
    padding-right: 24px;
  }
  @media (min-width: 1024px) {
    padding-left: 32px;
    padding-right: 32px;
  }
`

export const Section = styled.section<{ dark?: boolean }>`
  padding: 64px 0;
  ${props => props.dark && css`
    background: ${t.bgDark};
    color: ${t.white};
    position: relative;
    overflow: hidden;
  `}
  @media (min-width: 640px) { padding: 80px 0; }
  @media (min-width: 1024px) { padding: 112px 0; }
`

// Eyebrow 改成 pill 徽章（白底淡邊 + 細字）
export const Eyebrow = styled.span<{ onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  ${props => props.onDark ? css`
    background: ${t.white8};
    color: ${t.blue100};
    border: 1px solid ${t.white12};
  ` : css`
    background: ${t.white80};
    color: ${t.slate600};
    border: 1px solid ${t.borderSoft};
    box-shadow: 0 12px 30px rgba(15,23,42,0.06);
    backdrop-filter: blur(6px);
  `}
`

// 小標題（不含 badge）— 給區段輔助用
export const SectionTag = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: ${t.blue600};
`

export const Heading = styled.h2<{ size?: 'lg' | 'xl' | '2xl' | '3xl' | 'hero', onDark?: boolean }>`
  font-family: ${t.fontHeading};
  font-weight: 700;
  margin: 0;
  color: ${props => props.onDark ? t.white : t.slate950};
  ${props => {
    switch (props.size) {
    case 'hero': return css`
        font-size: 3rem;
        line-height: 0.95;
        letter-spacing: -0.06em;
        @media (min-width: 640px) { font-size: 3.75rem; }
        @media (min-width: 1024px) { font-size: 4.5rem; }
      `
    case '3xl': return css`
        font-size: 2.5rem;
        line-height: 1.05;
        letter-spacing: -0.05em;
        @media (min-width: 640px) { font-size: 3rem; }
        @media (min-width: 1024px) { font-size: 3.75rem; }
      `
    case '2xl': return css`
        font-size: 2rem;
        line-height: 1.1;
        letter-spacing: -0.04em;
        @media (min-width: 640px) { font-size: 2.5rem; }
      `
    case 'xl': return css`
        font-size: 1.75rem;
        line-height: 1.15;
        letter-spacing: -0.04em;
        @media (min-width: 640px) { font-size: 2.25rem; }
      `
    case 'lg':
    default: return css`
        font-size: 1.5rem;
        line-height: 1.2;
        letter-spacing: -0.03em;
        @media (min-width: 640px) { font-size: 1.875rem; }
      `
    }
  }}
`

export const Lead = styled.p<{ onDark?: boolean }>`
  font-size: 1.125rem;
  line-height: 1.75;
  color: ${props => props.onDark ? 'rgba(219,234,254,0.78)' : t.slate600};
  @media (min-width: 640px) {
    font-size: 1.25rem;
    line-height: 2;
  }
`

export const Body = styled.p<{ onDark?: boolean }>`
  font-size: 1rem;
  line-height: 1.75;
  color: ${props => props.onDark ? 'rgba(219,234,254,0.78)' : t.slate500};
  @media (min-width: 640px) {
    font-size: 1.0625rem;
  }
`

export const Caption = styled.p<{ onDark?: boolean }>`
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${props => props.onDark ? 'rgba(219,234,254,0.68)' : t.slate500};
`

// ===== 按鈕：原站主要 CTA 是 slate-950 底 + 白字，hover 藍 =====

export const PrimaryBtn = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  padding: 0 32px;
  border-radius: 999px;
  background: ${t.slate950};
  color: ${t.white};
  font-family: ${t.fontBody};
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: -0.005em;
  text-decoration: none;
  box-shadow: ${t.shadowBtn};
  transition: all 0.3s ease;
  &:hover {
    background: ${t.blue600};
    transform: translateY(-2px);
  }
`

// Nav 用（小）
export const PrimaryBtnSm = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 999px;
  background: ${t.slate950};
  color: ${t.white};
  font-family: ${t.fontBody};
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  box-shadow: ${t.shadowBtnSm};
  transition: all 0.3s ease;
  &:hover {
    background: ${t.blue600};
    transform: translateY(-2px);
  }
`

// 反白按鈕（用在深底上）
export const InverseBtn = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  padding: 0 32px;
  border-radius: 999px;
  background: ${t.white};
  color: ${t.slate950};
  font-family: ${t.fontBody};
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: ${t.shadowBtnLight};
  transition: all 0.3s ease;
  &:hover {
    background: ${t.blue100};
    transform: translateY(-2px);
  }
`

// ===== 晶片／卡片 =====

export const Chip = styled.span<{ onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  ${props => props.onDark ? css`
    background: ${t.white8};
    color: ${t.blue100};
    border: 1px solid ${t.white12};
  ` : css`
    background: ${t.white92};
    color: ${t.slate700};
    border: 1px solid ${t.borderSoft};
    box-shadow: 0 12px 30px rgba(15,23,42,0.05);
  `}
`

export const Card = styled.div`
  background: ${t.white};
  border: 1px solid ${t.borderSoft};
  border-radius: ${t.radiusXl};
  padding: 24px;
  box-shadow: ${t.shadowCard};
  @media (min-width: 640px) {
    padding: 32px;
  }
`

export const SoftCard = styled.div`
  background: rgba(248,250,252,0.9);
  border: 1px solid ${t.borderSoft};
  border-radius: 1.35rem;
  padding: 16px;
  color: ${t.slate700};
`

export const DashedCard = styled.div`
  background: rgba(239,246,255,0.7);
  border: 1px dashed ${t.blue200};
  border-radius: ${t.radiusXl};
  padding: 22px 26px;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${t.slate950};
`

export const DarkCard = styled.div`
  position: relative;
  overflow: hidden;
  background: ${t.slate950};
  border: 1px solid ${t.borderSoft};
  border-radius: ${t.radius2xl};
  color: ${t.white};
  padding: 24px;
  box-shadow: ${t.shadowDarkCard};
  @media (min-width: 640px) {
    padding: 32px 40px;
  }
  @media (min-width: 1024px) {
    padding: 40px 48px;
  }
`

// ===== grid / stack =====

export const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

// 編號徽章：原站是深色 rounded-2xl 方塊 + 白字（不是藍圓圈）
export const NumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 1rem;
  background: ${t.slate950};
  color: ${t.white};
  font-family: ${t.fontHeading};
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: ${t.shadowBadge};
`

export const NumBadgeLg = styled(NumBadge)`
  width: 56px;
  height: 56px;
  border-radius: 1rem;
  font-size: 1.125rem;
`

export const HStack = styled.div<{ gap?: number, align?: string, wrap?: boolean }>`
  display: flex;
  align-items: ${props => props.align || 'center'};
  gap: ${props => props.gap ?? 12}px;
  flex-wrap: ${props => props.wrap ? 'wrap' : 'nowrap'};
`

export const VStack = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap ?? 16}px;
`

export const Divider = styled.div<{ onDark?: boolean }>`
  height: 1px;
  width: 100%;
  background: ${props => props.onDark ? 'rgba(255,255,255,0.1)' : t.slate200};
`
